import { Injectable } from '@angular/core';
import { AudioService } from './audio.service';
import { RecordingStorageService } from './recording-storage.service';
import { FileDownloadService } from './file-download.service';
import { FailedRecording, AudioSegment } from '../interfaces/recording-storage.interface';
import * as moment from 'moment';

export interface DownloadLogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
  details?: any;
  stackTrace?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RecordingDownloadService {
  private logEntries: DownloadLogEntry[] = [];

  constructor(
    private audioService: AudioService,
    private recordingStorageService: RecordingStorageService,
    private fileDownloadService: FileDownloadService
  ) {}

  getLogs(): DownloadLogEntry[] {
    return [...this.logEntries];
  }

  clearLogs(): void {
    this.logEntries = [];
  }

  private addLog(level: DownloadLogEntry['level'], message: string, details?: any, error?: any): void {
    const timestamp = new Date().toISOString();
    const entry: DownloadLogEntry = {
      timestamp,
      level,
      message,
      details
    };

    if (error) {
      entry.stackTrace = this.getStackTrace(error);
      if (error?.stack) {
        entry.stackTrace = error.stack;
      } else if (error?.error?.stack) {
        entry.stackTrace = error.error.stack;
      }
      entry.details = {
        ...details,
        error: {
          message: error?.message || String(error),
          name: error?.name,
          ...(error?.errorCode && { errorCode: error.errorCode }),
          ...(error?.status && { status: error.status }),
          ...(error?.statusText && { statusText: error.statusText })
        }
      };
    }

    this.logEntries.push(entry);
  }

  private getStackTrace(error: any): string | undefined {
    if (error?.stack) {
      return error.stack;
    }
    if (error?.error?.stack) {
      return error.error.stack;
    }
    try {
      throw new Error('Stack trace');
    } catch (e: any) {
      if (e.stack) {
        return e.stack.split('\n').slice(2).join('\n');
      }
    }
    return undefined;
  }

  // Download single recording as complete file
  async downloadRecording(recordingId: string, onLog?: (entry: DownloadLogEntry) => void): Promise<void> {
    this.clearLogs();
    this.addLog('info', `Starting download for recording: ${recordingId}`);
    
    try {
      this.addLog('info', 'Fetching recording metadata from storage...');
      const recording = await this.recordingStorageService.getRecording(recordingId);
      if (!recording) {
        throw new Error(`Recording ${recordingId} not found`);
      }
      this.addLog('success', `Recording metadata retrieved`, { 
        patientName: recording.patientName,
        createdAt: recording.createdAt,
        totalDuration: recording.totalDuration,
        segmentCount: recording.segmentIds?.length || 0
      });

      this.addLog('info', 'Fetching audio segments from storage...');
      const segments = await this.recordingStorageService.getRecordingSegments(recordingId);
      if (segments.length === 0) {
        throw new Error(`No segments found for recording ${recordingId}`);
      }
      this.addLog('success', `Retrieved ${segments.length} audio segments`, {
        segmentCount: segments.length,
        totalSize: segments.reduce((sum, s) => sum + (s.size || 0), 0)
      });

      // Recompile all segments into single audio file
      this.addLog('info', 'Starting audio recompilation process...');
      const compiledBlob = await this.recompileRecording(segments);
      this.addLog('success', `Audio recompilation complete`, {
        blobSize: compiledBlob.size,
        blobType: compiledBlob.type
      });

      // Get download filename
      const fileName = this.getDownloadFileName(recording);
      this.addLog('info', `Generated filename: ${fileName}`);

      // Trigger download via platform-aware service (web = <a download>, native = Filesystem + Share).
      this.addLog('info', 'Triggering download via FileDownloadService...');
      try {
        await this.fileDownloadService.download(compiledBlob, fileName);
        this.addLog('success', 'Download triggered successfully', { fileName });
      } catch (downloadError: any) {
        this.addLog('error', 'Failed to trigger download', { fileName }, downloadError);
        const error = new Error(`Failed to trigger download: ${downloadError?.message || String(downloadError)}`);
        (error as any).originalError = downloadError;
        (error as any).stack = downloadError?.stack || error.stack;
        throw error;
      }

      this.addLog('success', `Download process completed successfully: ${fileName}`);
      
      // Notify log callback if provided
      if (onLog) {
        this.logEntries.forEach(entry => onLog(entry));
      }
    } catch (error) {
      this.addLog('error', 'Download process failed', { recordingId }, error);
      
      // Notify log callback if provided
      if (onLog) {
        this.logEntries.forEach(entry => onLog(entry));
      }
      
      console.error('Error downloading recording:', error);
      throw error;
    }
  }

  // Recompile all segments into single audio file
  async recompileRecording(segments: AudioSegment[]): Promise<Blob> {
    try {
      // Sort segments by order
      const sortedSegments = [...segments].sort((a, b) => a.order - b.order);
      this.addLog('info', `Sorted ${sortedSegments.length} segments by order`);

      // Decode all segments to AudioBuffer
      const audioBuffers: AudioBuffer[] = [];
      let decodedCount = 0;
      let skippedCount = 0;
      
      for (let i = 0; i < sortedSegments.length; i++) {
        const segment = sortedSegments[i];
        try {
          this.addLog('info', `Decoding segment ${i + 1}/${sortedSegments.length} (ID: ${segment.id})`, {
            segmentSize: segment.size,
            segmentOrder: segment.order
          });
          
          // Blob should be valid after retrieval fix, but check for old segments
          if (!segment.blob) {
            throw new Error('Segment blob is missing');
          }
          
          const audioBuffer = await this.audioService.decodeAudioData(segment.blob);
          audioBuffers.push(audioBuffer);
          decodedCount++;
          this.addLog('success', `Segment ${i + 1} decoded successfully`, {
            duration: audioBuffer.duration,
            sampleRate: audioBuffer.sampleRate,
            channels: audioBuffer.numberOfChannels
          });
        } catch (error: any) {
          skippedCount++;
          const errorMessage = error?.message || String(error);
          
          // Enhanced error logging
          if (errorMessage.includes('not accessible') || errorMessage.includes('NotFoundError') || errorMessage.includes('missing')) {
            this.addLog('error', `Blob access error for segment ${i + 1} (ID: ${segment.id})`, {
              segmentOrder: segment.order,
              errorType: 'Blob Inaccessibility',
              suggestion: errorMessage.includes('missing') 
                ? 'Segment blob is missing. This may be an old segment that needs to be re-stored with the new ArrayBuffer format.'
                : 'Blob became inaccessible. New recordings are stored as ArrayBuffer to prevent this issue.'
            }, error);
          } else {
            this.addLog('warn', `Failed to decode segment ${i + 1} (ID: ${segment.id}), skipping`, {
              segmentOrder: segment.order
            }, error);
          }
          // Continue with other segments
        }
      }

      if (audioBuffers.length === 0) {
        throw new Error('No segments could be decoded');
      }

      this.addLog('info', `Decoding complete: ${decodedCount} successful, ${skippedCount} skipped`);

      // Concatenate all audio buffers
      this.addLog('info', 'Concatenating audio buffers...');
      const concatenatedBuffer = this.audioService.concatenateAudioBuffers(audioBuffers);
      this.addLog('success', 'Audio buffers concatenated', {
        totalDuration: concatenatedBuffer.duration,
        sampleRate: concatenatedBuffer.sampleRate,
        channels: concatenatedBuffer.numberOfChannels
      });

      // Encode to WAV blob
      this.addLog('info', 'Encoding audio buffer to WAV blob...');
      const compiledBlob = await this.audioService.encodeAudioBufferToBlob(concatenatedBuffer);
      this.addLog('success', 'WAV encoding complete', {
        blobSize: compiledBlob.size,
        blobType: compiledBlob.type
      });

      return compiledBlob;
    } catch (error) {
      this.addLog('error', 'Audio recompilation failed', {}, error);
      console.error('Error recompiling recording:', error);
      throw error;
    }
  }

  // Get download filename
  getDownloadFileName(recording: FailedRecording): string {
    const patientName = recording.patientName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'unknown';
    const date = moment(recording.createdAt).format('YYYY-MM-DD_HH-mm-ss');
    const duration = Math.round(recording.totalDuration);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    const durationStr = `${minutes}m${seconds}s`;

    return `recording_${patientName}_${date}_${durationStr}.wav`;
  }
}

